import { resourceFromAttributes } from "@opentelemetry/resources";
import type { DetectedResourceAttributes } from "@opentelemetry/resources/build/src/types";
import UAParser from "ua-parser-js";
const parsedUserAgent = getParsedUserAgent();
// Frontend prefixes have been added to any attributes where there might be ambiguity about
// whether it comes from backend or frontend spans. This isn't strictly needed, but is a nice affordance to
// help people when they build queries in downstream tools.
const attributes: DetectedResourceAttributes = {
    "frontend.user_agent": parsedUserAgent.userAgent,
    "browser.name": parsedUserAgent.browserName,
    "browser.version": parsedUserAgent.browserVersion,
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    "browser.mobile": parsedUserAgent.userAgent.includes("Mobi"),
    "frontend.os.name": parsedUserAgent.osName,
    "frontend.os.version": parsedUserAgent.osVersion,
    "frontend.cpu.architecture": parsedUserAgent.cpuArchitecture,
    "frontend.device.type.original": parsedUserAgent.deviceTypeOriginal,
    "frontend.device.type": parsedUserAgent.deviceType,
    "frontend.device.model": parsedUserAgent.deviceModel,
    "frontend.device.vendor": parsedUserAgent.deviceVendor,
    "browser.engine.name": parsedUserAgent.browserEngineName,
    "browser.engine.version": parsedUserAgent.browserEngineVersion,
    "browser.touch_screen_enabled": navigator.maxTouchPoints > 0,
    "browser.language": navigator.language,
    "browser.timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    "screen.width": window.screen.width,
    "screen.height": window.screen.height,
    "frontend.network.effectiveType": getEffectiveNetworkType(),
};
function getEffectiveNetworkType() {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const extendedNavigator = navigator as ExtendedNavigator;
    return extendedNavigator.connection?.effectiveType ?? "unknown";
}
type NetworkInformationEffectiveType = "slow-2g" | "2g" | "3g" | "4g";
type ExtendedNavigator = Navigator & {
    // Limited availability: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
    connection?: NetworkInformation;
};
type NetworkInformation = {
    // Limited availability: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/effectiveType
    effectiveType?: NetworkInformationEffectiveType;
};
function getParsedUserAgent() {
    const parsedUserAgent = new UAParser(navigator.userAgent);
    const browser = parsedUserAgent.getBrowser();
    const os = parsedUserAgent.getOS();
    const cpu = parsedUserAgent.getCPU();
    const device = parsedUserAgent.getDevice();
    const engine = parsedUserAgent.getEngine();
    return {
        browserName: browser.name ?? "unknown",
        browserVersion: browser.version ?? "unknown",
        osName: os.name ?? "unknown",
        osVersion: os.version ?? "unknown",
        cpuArchitecture: cpu.architecture ?? "unknown",
        // For desktops, the device type isn't included in the user agent so the library returns unknown
        // If the device type wasn't detected, we can infer that the type is "desktop"
        // See https://github.com/faisalman/ua-parser-js/issues/182
        deviceType: device.type ?? "desktop",
        // For completeness, we also return the original device type in case the logic for
        // determining desktop is ever incorrect.
        deviceTypeOriginal: device.type ?? "unknown",
        deviceModel: device.model ?? "unknown",
        deviceVendor: device.vendor ?? "unknown",
        browserEngineName: engine.name ?? "unknown",
        browserEngineVersion: engine.version ?? "unknown",
        userAgent: parsedUserAgent.getUA(),
    };
}
export const browserAttributes = resourceFromAttributes(attributes);
